import type { Lang } from '../../types/funnel';
import type { LegalDocument } from '../../types/legal';

const contactLines = ['InstaMovers Florida LLC', 'Email: instamoversfl@gmail.com', 'Telephone: +1 (656) 268-0800', 'Website: https://floridalocalmovers.instamoversfl.com'];
const contactLinesEs = ['InstaMovers Florida LLC', 'Correo electrónico: instamoversfl@gmail.com', 'Teléfono: +1 (656) 268-0800', 'Sitio web: https://floridalocalmovers.instamoversfl.com'];

/** Owner-provided Privacy Policy text (published verbatim in English). Spanish is an equivalent translation. */
export const privacyPolicy: Record<Lang, LegalDocument> = {
  en: {
    title: 'Privacy Policy',
    effective: 'Effective date: October 6, 2026',
    sections: [
      {
        heading: 'Who We Are',
        body: [
          'This website is operated by InstaMovers Florida LLC. Florida Local Movers is a moving service website operated by InstaMovers Florida LLC. This Privacy Policy explains how we collect, use and protect information provided through this website and our moving service communications.',
        ],
      },
      {
        heading: 'Information We Collect',
        body: [
          'We collect information you provide when requesting a moving quote, contacting us or arranging services. This may include your name, email address, telephone number, moving origin and destination, ZIP codes, preferred moving date, inventory details and other information you choose to provide.',
          'We also collect your communication preferences and records of any SMS consent you provide or withdraw.',
          'Depending on the website features in use, technical information may include your IP address, browser, device information, pages visited and cookie or similar technology data.',
        ],
      },
      {
        heading: 'How We Use Information',
        body: [
          'We use information to respond to inquiries, prepare moving estimates, coordinate bookings and services, provide customer support, maintain business records, improve the website and protect against fraud or misuse.',
          'We use your telephone number for SMS communications only according to the consent you provide. Providing a telephone number or submitting a quote request does not, by itself, enroll you in marketing text messages.',
        ],
      },
      {
        heading: 'SMS Consent and Communications',
        body: [
          'You may separately choose to receive non-marketing text messages about your requested moving quote, booking, appointment coordination and moving service updates, and marketing text messages about offers, discounts and promotional follow-up.',
          'SMS consent is voluntary. Consent boxes are optional and are not selected by default. You can request a quote or purchase services without agreeing to SMS messages.',
          'Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe or HELP for assistance. You may also contact instamoversfl@gmail.com or +1 (656) 268-0800.',
        ],
      },
      {
        heading: 'Mobile Information and SMS Consent Protection',
        body: [
          'No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. Information sharing to subcontractors in support services, such as customer service, is permitted. All other use case categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.',
        ],
      },
      {
        heading: 'Service Providers and Other Disclosures',
        body: [
          'We may use service providers to operate the website, maintain customer records, deliver communications and support our moving services. They may process information only as necessary to provide those services, subject to the protections described in this policy.',
          'We may disclose information when required by law or when reasonably necessary to protect rights, safety or the security of our services. SMS consent is not transferable to another business for its marketing.',
        ],
      },
      {
        heading: 'Cookies and Advertising Technologies',
        body: [
          'Where implemented, cookies and similar technologies may support website functionality, analytics and advertising measurement. You may manage cookies through your browser settings and any privacy controls available on the website.',
        ],
      },
      {
        heading: 'Security and Retention',
        body: [
          'We use reasonable safeguards appropriate to the information we handle. No method of electronic transmission or storage is completely secure.',
          'We retain information as reasonably necessary to provide services, maintain consent and opt-out records, resolve disputes and meet applicable recordkeeping obligations.',
        ],
      },
      {
        heading: 'Your Choices',
        body: [
          'You may contact us to request access to, correction of or deletion of your information, subject to applicable law and necessary recordkeeping. You may withdraw SMS consent by replying STOP. We retain suppression information as necessary to honor your opt-out.',
        ],
      },
      {
        heading: 'Children',
        body: [
          'This website and our SMS program are intended for adults aged 18 or older. We do not knowingly collect personal information from children under 18 through this service.',
        ],
      },
      {
        heading: 'Updates',
        body: ['We may update this policy. Changes will be posted on this page with a revised effective date.'],
      },
      {
        heading: 'Contact',
        body: [{ lines: contactLines }, { before: 'For SMS program terms, see our ', linkText: 'Terms & Conditions', to: '/terms-and-conditions', after: '.' }],
      },
    ],
  },
  es: {
    title: 'Política de Privacidad',
    effective: 'Fecha de entrada en vigor: 6 de octubre de 2026',
    sections: [
      {
        heading: 'Quiénes somos',
        body: [
          'Este sitio web es operado por InstaMovers Florida LLC. Florida Local Movers es un sitio web de servicios de mudanza operado por InstaMovers Florida LLC. Esta Política de Privacidad explica cómo recopilamos, usamos y protegemos la información proporcionada a través de este sitio web y de nuestras comunicaciones sobre servicios de mudanza.',
        ],
      },
      {
        heading: 'Información que recopilamos',
        body: [
          'Recopilamos la información que usted proporciona al solicitar una cotización de mudanza, contactarnos o coordinar servicios. Esto puede incluir su nombre, correo electrónico, número de teléfono, origen y destino de la mudanza, códigos postales, fecha preferida de mudanza, detalles del inventario y otra información que decida proporcionar.',
          'También recopilamos sus preferencias de comunicación y los registros de cualquier consentimiento para SMS que otorgue o retire.',
          'Según las funciones del sitio web que se utilicen, la información técnica puede incluir su dirección IP, navegador, información del dispositivo, páginas visitadas y datos de cookies o tecnologías similares.',
        ],
      },
      {
        heading: 'Cómo usamos la información',
        body: [
          'Usamos la información para responder consultas, preparar estimados de mudanza, coordinar reservas y servicios, brindar atención al cliente, mantener registros comerciales, mejorar el sitio web y proteger contra fraude o uso indebido.',
          'Usamos su número de teléfono para comunicaciones por SMS únicamente según el consentimiento que usted otorgue. Proporcionar un número de teléfono o enviar una solicitud de cotización no lo inscribe, por sí solo, en mensajes de texto de marketing.',
        ],
      },
      {
        heading: 'Consentimiento y comunicaciones por SMS',
        body: [
          'Usted puede elegir por separado recibir mensajes de texto que no son de marketing sobre su cotización de mudanza solicitada, reserva, coordinación de citas y actualizaciones del servicio de mudanza, y mensajes de texto de marketing sobre ofertas, descuentos y seguimiento promocional.',
          'El consentimiento para SMS es voluntario. Las casillas de consentimiento son opcionales y no están seleccionadas de forma predeterminada. Puede solicitar una cotización o contratar servicios sin aceptar mensajes SMS.',
          'La frecuencia de los mensajes varía. Pueden aplicar tarifas de mensajes y datos. Responda STOP para cancelar la suscripción o HELP para obtener ayuda. También puede comunicarse a instamoversfl@gmail.com o al +1 (656) 268-0800.',
        ],
      },
      {
        heading: 'Protección de la información móvil y del consentimiento para SMS',
        body: [
          'No se compartirá información móvil con terceros ni afiliados con fines de marketing o promoción. Se permite compartir información con subcontratistas que prestan servicios de apoyo, como atención al cliente. Todas las demás categorías de uso excluyen los datos de inscripción y el consentimiento del originador de mensajes de texto; esta información no se compartirá con ningún tercero.',
        ],
      },
      {
        heading: 'Proveedores de servicios y otras divulgaciones',
        body: [
          'Podemos utilizar proveedores de servicios para operar el sitio web, mantener registros de clientes, enviar comunicaciones y apoyar nuestros servicios de mudanza. Solo pueden procesar la información en la medida necesaria para prestar esos servicios, sujeto a las protecciones descritas en esta política.',
          'Podemos divulgar información cuando la ley lo exija o cuando sea razonablemente necesario para proteger derechos, la seguridad o la protección de nuestros servicios. El consentimiento para SMS no es transferible a otra empresa para su marketing.',
        ],
      },
      {
        heading: 'Cookies y tecnologías publicitarias',
        body: [
          'Cuando se implementen, las cookies y tecnologías similares pueden apoyar el funcionamiento del sitio web, la analítica y la medición publicitaria. Puede administrar las cookies desde la configuración de su navegador y cualquier control de privacidad disponible en el sitio web.',
        ],
      },
      {
        heading: 'Seguridad y conservación',
        body: [
          'Utilizamos medidas de protección razonables y adecuadas a la información que manejamos. Ningún método de transmisión o almacenamiento electrónico es completamente seguro.',
          'Conservamos la información en la medida razonablemente necesaria para prestar servicios, mantener registros de consentimiento y de cancelación, resolver disputas y cumplir con las obligaciones de conservación de registros aplicables.',
        ],
      },
      {
        heading: 'Sus opciones',
        body: [
          'Puede comunicarse con nosotros para solicitar el acceso, la corrección o la eliminación de su información, sujeto a la ley aplicable y a la conservación de registros necesaria. Puede retirar su consentimiento para SMS respondiendo STOP. Conservamos la información de exclusión necesaria para respetar su cancelación.',
        ],
      },
      {
        heading: 'Menores de edad',
        body: [
          'Este sitio web y nuestro programa de SMS están dirigidos a adultos de 18 años o más. No recopilamos a sabiendas información personal de menores de 18 años a través de este servicio.',
        ],
      },
      {
        heading: 'Actualizaciones',
        body: ['Podemos actualizar esta política. Los cambios se publicarán en esta página con una fecha de entrada en vigor revisada.'],
      },
      {
        heading: 'Contacto',
        body: [{ lines: contactLinesEs }, { before: 'Para los términos del programa de SMS, consulte nuestros ', linkText: 'Términos y Condiciones', to: '/terms-and-conditions', after: '.' }],
      },
    ],
  },
};
